import { ClearRounded, DeleteOutlineRounded, FileDownloadRounded, RefreshRounded, UploadRounded } from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import ExcelJS from "exceljs";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { useEffect, useMemo, useState } from "react";

const UPLOAD_COLUMNS = ["option_id", "channel", "region", "warehouse", "size", "total_qty"];

const normalizeHeader = (value) =>
	String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");

const formatCellValue = (value) => {
	if (value == null) return "";
	if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
	if (typeof value === "object") {
		if (value?.text != null) return String(value.text).trim();
		if (value?.result != null) return String(value.result).trim();
	}
	return String(value).trim();
};

const UploadModal = ({ modal, setModal, callbackFun }) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [fileName, setFileName] = useState("");
	const [headers, setHeaders] = useState([]);
	const [sourceRows, setSourceRows] = useState([]);
	const [columnMap, setColumnMap] = useState({});
	const [publish, setPublish] = useState(false);
	const [previewRows, setPreviewRows] = useState([]);
	const [rowFeedback, setRowFeedback] = useState({});

	const resetState = () => {
		setLoading(false);
		setError("");
		setFileName("");
		setHeaders([]);
		setSourceRows([]);
		setColumnMap({});
		setPublish(false);
		setPreviewRows([]);
		setRowFeedback({});
	};

	const handleClose = () => {
		resetState();
		setModal(false);
	};

	const parseExcelFile = async (file) => {
		const workbook = new ExcelJS.Workbook();
		await workbook.xlsx.load(await file.arrayBuffer());
		const worksheet = workbook.worksheets[0];
		if (!worksheet) return { headers: [], rows: [] };

		const parsedHeaders = worksheet
			.getRow(1)
			.values.slice(1)
			.map((value, index) => formatCellValue(value) || `Column ${index + 1}`);

		const parsedRows = [];
		worksheet.eachRow((row, rowNumber) => {
			if (rowNumber === 1) return;
			parsedRows.push(row.values.slice(1));
		});

		return { headers: parsedHeaders, rows: parsedRows };
	};

	const getDefaultColumnMap = (headerList) => {
		const nextMap = {};
		UPLOAD_COLUMNS.forEach((key) => {
			const index = headerList.findIndex((header) => normalizeHeader(header) === key);
			nextMap[key] = index >= 0 ? String(index) : "";
		});
		return nextMap;
	};

	const buildMappedRows = (rows = sourceRows, map = columnMap) => {
		const missing = UPLOAD_COLUMNS.filter((key) => map[key] === "" || map[key] == null);
		if (missing.length) {
			throw new Error(`Map all required columns: ${missing.join(", ")}`);
		}

		return rows
			.map((row, index) => {
				const option_id = formatCellValue(row?.[Number(map.option_id)]);
				const channel = formatCellValue(row?.[Number(map.channel)]);
				const region = formatCellValue(row?.[Number(map.region)]) || "ALL";
				const warehouse = formatCellValue(row?.[Number(map.warehouse)]);
				const size = formatCellValue(row?.[Number(map.size)]).toUpperCase();
				const total_qty = Number(formatCellValue(row?.[Number(map.total_qty)]));

				const issues = [];
				if (!option_id) issues.push("Missing option_id");
				if (!channel) issues.push("Missing channel");
				if (!warehouse) issues.push("Missing warehouse");
				if (!size) issues.push("Missing size");
				if (!Number.isFinite(total_qty) || total_qty < 0) issues.push("Invalid total_qty");

				return {
					__rowId: `buy-qty-upload-${index}`,
					option_id,
					channel,
					region,
					warehouse,
					size,
					total_qty: Number.isFinite(total_qty) ? Math.floor(total_qty) : 0,
					__isValid: issues.length === 0,
					__status: issues.length ? "Invalid" : "Valid",
					__note: issues.join("; "),
				};
			})
			.filter((row) => row.option_id || row.channel || row.warehouse || row.size);
	};

	const handleFileChange = async (event) => {
		const selectedFile = event.target.files?.[0];
		event.target.value = "";
		if (!selectedFile) return;

		setLoading(true);
		setError("");

		try {
			const parsedData = await parseExcelFile(selectedFile);
			const nextMap = getDefaultColumnMap(parsedData.headers);
			setFileName(selectedFile.name);
			setHeaders(parsedData.headers);
			setSourceRows(parsedData.rows);
			setColumnMap(nextMap);
			setRowFeedback({});
			setPreviewRows(buildMappedRows(parsedData.rows, nextMap));
		} catch (uploadError) {
			resetState();
			setError(uploadError.message || "Unable to read the selected file.");
		} finally {
			setLoading(false);
		}
	};

	const handlePreview = () => {
		setLoading(true);
		setError("");
		try {
			setPreviewRows(buildMappedRows(sourceRows, columnMap));
		} catch (previewError) {
			setError(previewError.message || "Unable to preview file.");
			setPreviewRows([]);
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadFormat = async () => {
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet("Buy Qty Upload");
		worksheet.addRow(UPLOAD_COLUMNS);
		worksheet.addRow(["RR-30000-OPT-10291", "EBO", "ALL", "GGN", "M", 10]);

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = "buy_qty_sheet_upload_format.xlsx";
		link.click();
		URL.revokeObjectURL(link.href);
	};

	const handleFormSubmit = async (event) => {
		event.preventDefault();
		if (typeof callbackFun !== "function") return;

		setLoading(true);
		try {
			const validRows = displayPreviewRows.filter((row) => row.__isValid !== false);
			const uploadResult = await callbackFun(validRows, { publish });
			const missingOptionIds =
				uploadResult?.result?.missing_option_ids || uploadResult?.result?.missing_color_ids || [];

			if (missingOptionIds.length) {
				const missingSet = new Set(missingOptionIds.map(String));
				const nextFeedback = {};
				previewRows.forEach((row) => {
					if (missingSet.has(String(row.option_id))) {
						nextFeedback[row.__rowId] = {
							__isValid: false,
							__status: "Missing",
							__note: `${row.option_id}: Option not found`,
						};
					}
				});
				setRowFeedback(nextFeedback);
				return;
			}

			if (uploadResult?.status === "error") {
				setError(uploadResult?.message || "Upload failed.");
				return;
			}

			if (uploadResult !== false) handleClose();
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteRow = (rowId) => {
		setPreviewRows((prev) => prev.filter((row) => row.__rowId !== rowId));
	};

	const displayPreviewRows = useMemo(
		() =>
			previewRows.map((row) => ({
				...row,
				...(rowFeedback[row.__rowId] ?? {}),
			})),
		[previewRows, rowFeedback],
	);

	const canPublish = displayPreviewRows.length > 0 && displayPreviewRows.every((row) => row.__isValid !== false);

	useEffect(() => {
		if (!canPublish && publish) setPublish(false);
	}, [canPublish, publish]);

	const uploadPreviewColumns = useMemo(
		() => [
			{
				key: "__upload_status",
				name: "STATUS",
				headerAlign: "center",
				align: "center",
				minWidth: 100,
				renderCell: ({ row }) => (
					<Box color={row.__isValid === false ? "error.main" : "success.main"} fontWeight={600}>
						{row.__status || "Valid"}
					</Box>
				),
			},
			...UPLOAD_COLUMNS.map((key) => ({
				key,
				name: key.toUpperCase(),
				headerAlign: "center",
				align: "center",
				minWidth: 110,
			})),
			{
				key: "__note",
				name: "NOTE",
				headerAlign: "center",
				align: "center",
				minWidth: 180,
				renderCell: ({ row }) => (
					<Typography variant="body2" color={row.__isValid === false ? "error.main" : "text.secondary"}>
						{row.__note || ""}
					</Typography>
				),
			},
			{
				key: "__delete",
				name: "DELETE",
				headerAlign: "center",
				align: "center",
				minWidth: 90,
				renderCell: ({ row }) => (
					<IconButton size="small" color="error" onClick={() => handleDeleteRow(row.__rowId)}>
						<DeleteOutlineRounded fontSize="small" />
					</IconButton>
				),
			},
		],
		[],
	);

	const validCount = displayPreviewRows.filter((row) => row.__isValid !== false).length;

	return (
		<Dialog maxWidth="xl" fullWidth scroll="paper" open={modal} onClose={handleClose} PaperProps={{ sx: { height: "82vh" } }}>
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="h6">Upload Buy Qty</Typography>
				<IconButton onClick={handleClose}>
					<ClearRounded />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", pt: 1.5, pb: 1.5 }}>
				<Stack spacing={1.25} sx={{ minHeight: 0, flex: 1, overflow: "hidden" }}>
					{previewRows.length === 0 && (
						<Alert severity="info" sx={{ py: 0.5, alignItems: "center" }}>
							Upload Excel with columns: <strong>{UPLOAD_COLUMNS.join(", ")}</strong>
						</Alert>
					)}

					<Box>
						<Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
							<Button
								component="label"
								variant="contained"
								size="small"
								startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UploadRounded />}>
								Choose Excel
								<input hidden type="file" accept=".xlsx" onChange={handleFileChange} disabled={loading} />
							</Button>
							<Button variant="outlined" size="small" startIcon={<FileDownloadRounded />} onClick={handleDownloadFormat}>
								Upload Format
							</Button>
							<Button
								variant="contained"
								color="success"
								size="small"
								startIcon={<RefreshRounded />}
								onClick={handlePreview}
								disabled={loading || !headers.length}>
								Preview Rows
							</Button>
						</Stack>
						{fileName && (
							<Typography variant="body2" color="text.secondary" pt={1}>
								{`${fileName} - ${sourceRows.length} rows found`}
							</Typography>
						)}
					</Box>

					{headers.length > 0 && (
						<Box display="flex" flexWrap="wrap" gap={2}>
							{UPLOAD_COLUMNS.map((key) => (
								<FormControl key={key} size="small" sx={{ minWidth: 180 }}>
									<InputLabel id={`upload-${key}-label`}>{key}</InputLabel>
									<Select
										labelId={`upload-${key}-label`}
										label={key}
										value={columnMap[key] ?? ""}
										onChange={(event) => setColumnMap((prev) => ({ ...prev, [key]: event.target.value }))}>
										<MenuItem value="">Not mapped</MenuItem>
										{headers.map((header, index) => (
											<MenuItem key={`${header}-${index}`} value={String(index)}>
												{header}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							))}
						</Box>
					)}

					{error && <Alert severity="error">{error}</Alert>}

					{previewRows.length > 0 && (
						<Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
							<TanstackDatagrid
								columns={uploadPreviewColumns}
								pageData={displayPreviewRows}
								setPageData={setPreviewRows}
								getRowId={(row) => row.__rowId}
								disablePagination
								serverSidePagination={false}
								enableHorizontalScroll
								headerHeight={40}
								rowHeight={35}
								processRowUpdate={(newRow) => newRow}
								isCellEditable={({ column }) => !["__upload_status", "__note", "__delete"].includes(column.key)}
								children={
									<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
										{`${validCount} valid row(s) from ${sourceRows.length} source rows`}
									</Typography>
								}
							/>
						</Box>
					)}

					<FormControlLabel
						control={
							<Checkbox checked={publish} onChange={(event) => setPublish(event.target.checked)} disabled={!canPublish} />
						}
						label="Publish after upload"
					/>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button variant="outlined" onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleFormSubmit} disabled={loading || validCount === 0}>
					Save Upload
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default UploadModal;
